import isEmpty from 'lodash.isempty';
import type {TwoFactorAuthenticationChallenge} from '@api/recipient/api.d';
import {getValueForKey, setValueForKey} from '@utils/storageSelectors';
import {
    ONLY_DIGITS_REGEXP,
    MAX_PASSCODE_LENGTH,
    MIN_PASSCODE_LENGTH
} from './constants';

const STORAGE_KEY_2FA_COMPLETED = '2fa_completed';

export function set2FACompleted(transactionId: string, recipientId: string): void {
    const storageKey = `${STORAGE_KEY_2FA_COMPLETED}-${recipientId}-${transactionId}`;
    setValueForKey(storageKey, 'true');
}

export function getIs2FACompleted(transactionId: string | null, recipientId: string | null): boolean {
    if (!transactionId || !recipientId) {
        return false;
    }

    const storageKey = `${STORAGE_KEY_2FA_COMPLETED}-${recipientId}-${transactionId}`;
    return getValueForKey(storageKey) === 'true';
}

interface FormatMessageFn {
    (descriptor: {id: string; defaultMessage: string}, values?: Record<string, unknown>): string;
}

interface PasscodeValidationParams {
    passcode: string;
    formatMessage: FormatMessageFn;
}

export interface QnaFormItem extends Pick<TwoFactorAuthenticationChallenge, 'id'> {
    question: string;
    answer: string;
    isMasked: boolean;
}

interface QnaValidationParams {
    qna: QnaFormItem[];
}

export function getPasscodeErrorMessageKey({passcode = '', formatMessage}: PasscodeValidationParams): string | null {
    if (passcode.length < MIN_PASSCODE_LENGTH) {
        return formatMessage({id: 'oss.components.PanelForm.Field.minLength', defaultMessage: ''}, {
            minLength: MIN_PASSCODE_LENGTH
        });
    }

    if (passcode.length > MAX_PASSCODE_LENGTH) {
        return formatMessage({id: 'oss.components.PanelForm.Field.maxLength', defaultMessage: ''}, {
            maxLength: MAX_PASSCODE_LENGTH
        });
    }

    if (!ONLY_DIGITS_REGEXP.test(passcode)) {
        return formatMessage({id: 'oss.components.PanelForm.Field.onlyDigits', defaultMessage: ''});
    }

    return null;
}

export function getIsQnaValid({qna}: QnaValidationParams): boolean {
    return !isEmpty(qna) && qna.every(({answer}) => !!answer);
}

const PHONE_SUFFIX_LENGTH = 4;

export function getPhoneNumberSuffix(phone: string | null | undefined): string {
    if (!phone) {
        return '';
    }

    const digitsOnly = phone.replace(/\D/g, '');

    return digitsOnly.slice(-PHONE_SUFFIX_LENGTH);
}
